<template>
  <div class="flex min-h-12 flex-wrap items-center gap-2 text-blue-500 typo-body-2xl">
    <template
      v-for="option in filterOptions"
      :key="option.label"
    >
      <button
        v-if="option.show"
        class="h-fit rounded-sm px-2 transition-colors hover:bg-grey-100"
        :class="{ 'bg-grey-100': isActive(option.value) }"
        type="button"
        @click="onFilterClick(option.value)"
      >
        {{ option.label }}
      </button>
    </template>
    <div class="flex items-center gap-1">
      <Transition mode="out-in" name="fade">
        <button
          v-if="!isCustomActive"
          class="flex items-center gap-2 rounded-sm px-2 transition-colors hover:bg-grey-100"
          :class="{ 'bg-grey-100': isCustomActive }"
          type="button"
          @click="isCustomActive = true"
        >
          <span>{{ $t('chart_filter.individually') }}</span>
          <IconCalendar class="mb-px inline-block size-4" />
        </button>
        <div v-else class="flex items-center gap-3">
          <div class="grid grid-cols-[auto,1fr] items-center gap-1">
            <label for="date-range-input-from" class="text-dark typo-body-md">{{ $t('from') }}:</label>
            <input
              id="date-range-input-from"
              v-model="customStart"
              type="date"
              class="typo-body-lg accent-color"
              :min="minDate"
              :max="maxDate"
              @change="updateCustomFilter"
            />
            <label for="date-range-input-to" class="text-dark typo-body-md">{{ $t('to') }}:</label>
            <input
              id="date-range-input-to"
              v-model="customEnd"
              type="date"
              class="typo-body-lg accent-color"
              :min="minDate"
              :max="maxDate"
              @change="updateCustomFilter"
            />
          </div>
          <button
            v-if="customStart || customEnd"
            type="button"
            class="size-7 rounded-sm bg-grey-100 p-2 transition-colors hover:bg-grey-200"
            @click="customStart = '', customEnd = '', filter = null"
          >
            <IconReset class="size-full" />
          </button>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script lang="ts" setup>
const filter = defineModel<{
  start: Date | null
  end: Date
} | null>({ default: null })

const props = defineProps<{
  chartData: { cells: (string | number)[] }[]
}>()

const customStart = ref<string>('')
const customEnd = ref<string>('')
const isCustomActive = ref<boolean>(false)

const { t } = useI18n()

// Check if maxDate is after getYearStart for YTD visibility
const showYTD = computed(() => {
  if (!maxDate.value) return false
  const max = new Date(maxDate.value)
  const yearStart = getYearStart()
  return max > yearStart
})

const filterOptions = computed(() => [
  {
    label: 'YTD',
    value: getYearStart(),
    show: showYTD.value,
  },
  {
    label: t('chart_filter.one_year'),
    value: getYearsAgo(1),
    show: !minDate.value || getYearsAgo(1) >= new Date(minDate.value),
  },
  {
    label: t('chart_filter.three_years'),
    value: getYearsAgo(3),
    show: !minDate.value || getYearsAgo(3) >= new Date(minDate.value),
  },
  {
    label: t('chart_filter.ten_years'),
    value: getYearsAgo(10),
    show: !minDate.value || getYearsAgo(10) >= new Date(minDate.value),
  },
  {
    label: t('chart_filter.since_beginning'),
    value: null,
    show: true, // Always show "Seit Beginn"
  },
])

// Compute min and max dates from chart_data
const minDate = computed(() => {
  if (!Array.isArray(props.chartData) || props.chartData.length <= 1) {
    return null
  }
  const dates = props.chartData
    .slice(1) // Exclude header row
    .map(row => row.cells?.[0] ? new Date(row.cells[0]) : null)
    .filter(date => date && !isNaN(date.getTime()))
  const minDate = dates.length ? new Date(Math.min(...dates.map(d => d!.getTime()))) : null
  if (minDate) {
    minDate.setDate(minDate.getDate() - 1)
    return minDate.toISOString().split('T')[0]
  }
  return ''
})

const maxDate = computed(() => {
  if (!Array.isArray(props.chartData) || props.chartData.length <= 1) {
    return null
  }
  const dates = props.chartData
    .slice(1) // Exclude header row
    .map(row => row.cells?.[0] ? new Date(row.cells[0]) : null)
    .filter(date => date && !isNaN(date.getTime()))
  const maxDate = dates.length ? new Date(Math.max(...dates.map(d => d!.getTime()))) : null
  if (maxDate) {
    maxDate.setDate(maxDate.getDate() + 1)
    return maxDate.toISOString().split('T')[0]
  }
  return new Date().toISOString()
    .split('T')[0] // Default to today if no data
})

function getYearStart(): Date {
  const date = new Date(Date.UTC(new Date().getUTCFullYear(), 0, 1)) // January 1st of current year in UTC
  date.setUTCHours(0, 0, 0, 0) // Ensure midnight UTC
  return date
}

function getYearsAgo(years: number): Date {
  const date = new Date()
  date.setFullYear(date.getFullYear() - years)
  date.setHours(0, 0, 0, 0) // Normalize to midnight
  return date
}

function isActive(value: Date | null): boolean {
  if (isCustomActive.value) return false // Custom filter takes precedence
  if (!filter.value?.start && !value) return true // Both null (e.g. "Seit Beginn")
  if (!filter.value?.start || !value) return false // One is null, other isn't
  const filterDate = new Date(filter.value.start)
  const optionDate = new Date(value)
  return (
    filterDate.getFullYear() === optionDate.getFullYear()
    && filterDate.getMonth() === optionDate.getMonth()
    && filterDate.getDate() === optionDate.getDate()
  )
}

function onFilterClick(value: Date | null) {
  if (isActive(value)) {
    filter.value = null
    isCustomActive.value = false
    resetCustomInputs()
  } else {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const maxDateParsed = maxDate.value ? new Date(maxDate.value) : today
    // If maxDate is after today, use maxDate as end date
    const end = today < maxDateParsed ? maxDateParsed : today
    end.setHours(0, 0, 0, 0)
    filter.value = {
      start: value,
      end,
    }
    isCustomActive.value = false
    resetCustomInputs()
  }
}

function updateCustomFilter() {
  if (customStart.value && customEnd.value) {
    const start = new Date(customStart.value)
    const end = new Date(customEnd.value)
    if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && start <= end) {
      start.setHours(0, 0, 0, 0)
      end.setHours(0, 0, 0, 0)
      filter.value = {
        start,
        end,
      }
      isCustomActive.value = true
    }
  }
}

function resetCustomInputs() {
  customStart.value = ''
  customEnd.value = ''
}

watch(filter, (newFilter) => {
  if (!newFilter || isCustomActive.value) return
  resetCustomInputs()
}, { deep: true })
</script>

<style scoped>
/* Currently not working for input[type="date"] but may in the future */
.accent-color {
  accent-color: #005CA9; /* blue-500 */
}
</style>
